import isEqual from 'lodash-es/isEqual';
import Split from 'split.js';

import { on, unByKey } from 'cadenza/utils/event-util';
import { STATE, VIEW } from 'cadenza/map-dashboard/event-types';

import './map-dashboard.css';

const name = 'd-map-dashboard';

const DASHBOARD_LAYOUT_PARAM = 'dashboardLayout';
const DASHBOARD_SPLIT_SIZE_PARAM = 'dashboardSplitSize';

const DASHBOARD_ACTIVE_CLASS = 'dashboard-active';

class MapDashboard extends HTMLElement {

  constructor () {
    super();
    this._store = emptyStore();
  }

  connectedCallback () {
    this._eventKeys = [
      on(this, STATE.SELECTION_CHANGE, event => this._dashboardListener(event, event => {
        const eventLayers = Object.keys(event.detail);
        const currentEventLayersSelection = {};
        eventLayers.forEach(layerId => { currentEventLayersSelection[layerId] = this._store.selectedFeatureIds[layerId] || []; });

        this._updateLocalStorageState(event, currentEventLayersSelection, newEventLayersSelection => {
          eventLayers.forEach(layerId => { this._store.selectedFeatureIds[layerId] = newEventLayersSelection[layerId]; });
        });
      })),
      on(this, STATE.EXTENT_CHANGE, event => this._dashboardListener(event, event => {
        const { mapSrs, mapExtent } = event.detail;
        addParamsToUrl({ mapSrs, mapExtent });
        this.dispatchEvent(new CustomEvent(STATE.EXTENT_CHANGE));
      })),
      on(this, STATE.CONDITION_CHANGE, event => this._dashboardListener(event, () => {
        this.dispatchEvent(new CustomEvent(STATE.CONDITION_CHANGE, { detail: event.detail }));
      })),
      on(this, VIEW.TOGGLE_TABLE_VISIBILITY, event => this._dashboardListener(event, event => {
        if (!event.detail.isVisible && event.detail.viewId) {
          delete this._store.selectedFeatureIds[event.detail.viewId];
        }
        this.dispatchEvent(new CustomEvent(event.type, { detail: event.detail }));
      })),
      on(this, VIEW.LAYOUT_CHANGED, event => this._dashboardListener(event, event => {
        if (event.detail.shouldDashboardActivate != null) {
          const shouldDashboardAcivate = event.detail.shouldDashboardActivate;
          this.classList.toggle(DASHBOARD_ACTIVE_CLASS, shouldDashboardAcivate);
          if (shouldDashboardAcivate) {
            this._setupResizer();
          } else {
            this.removeResizer();
          }
          this.dispatchEvent(new CustomEvent(event.type, { detail: event.detail }));
        }

        if (event.detail.orientation != null) {
          const dashboardLayout = event.detail.orientation;
          this._setupResizer({ dashboardLayout });
          this._updateUrlState(event, DASHBOARD_LAYOUT_PARAM, 'horizontal', dashboardLayout);
        }
      }))
    ];
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
  }

  get selectedFeatureIDs () {
    return this._store.selectedFeatureIds;
  }

  _dashboardListener (event, delegateListener) {
    if (event.target !== this) {
      event.stopImmediatePropagation();
      delegateListener(event);
    }
  }

  _updateLocalStorageState (event, currentState, storeUpdater, newState = event.detail) {
    if (!isEqual(newState, currentState)) {
      storeUpdater(newState);
      this.dispatchEvent(new CustomEvent(event.type, { detail: newState }));
    }
  }

  _updateUrlState (event, paramName, defaultValue, newParamValue = event.detail) {
    const currentValue = getUrlParam(paramName);
    if (!isEqual(newParamValue, currentValue)) {
      toggleParam(paramName, newParamValue, !isEqual(defaultValue, newParamValue));
      this.dispatchEvent(new CustomEvent(event.type, { detail: newParamValue }));
    }
  }

  isActive () {
    return this.classList.contains(DASHBOARD_ACTIVE_CLASS);
  }

  _setupResizer ({ dashboardLayout = getUrlParam(DASHBOARD_LAYOUT_PARAM, 'horizontal'), mapViewSize = parseInt(getUrlParam(DASHBOARD_SPLIT_SIZE_PARAM, 50)) } = {}) {
    const mapView = this.querySelector('d-map-view');
    const tableView = this.querySelector('d-table-view');
    const tableViewOverlay = this.querySelector('.d-table-view--overlay');

    this.querySelector('.views-container').classList.toggle('with-vertical-layout', dashboardLayout === 'vertical'); // Check when opening the table

    this.removeResizer();

    const toggleTableViewOverlayToMakeIFrameSplitWorkInIE11 = isHidden => {
      tableViewOverlay.classList.toggle('transparent', !isHidden);
      tableViewOverlay.hidden = isHidden;
    };

    this._splitResizer = new Split([ mapView, tableView ], {
      sizes: [ mapViewSize, 100 - mapViewSize ],
      gutterSize: 1,
      direction: dashboardLayout,
      elementStyle: (dimension, elementSize, gutterSize, index) => {
        const view = index === 0 ? mapView : tableView;
        view.setAttribute('style', `flex-basis: ${elementSize}%`);
        return {};
      },
      onDragStart: () => {
        this._gutter.classList.toggle('moving', true);
        if (tableViewOverlay.hidden) {
          toggleTableViewOverlayToMakeIFrameSplitWorkInIE11(false);
        }
      },
      onDragEnd: sizes => {
        this._gutter.classList.toggle('moving', false);
        if (tableViewOverlay.classList.contains('transparent')) {
          toggleTableViewOverlayToMakeIFrameSplitWorkInIE11(true);
        }
        this._updateUrlState({ type: VIEW.LAYOUT_CHANGED }, DASHBOARD_SPLIT_SIZE_PARAM, 50, Math.round(sizes[0]));
      }
    });
    this._gutter = this._splitResizer.pairs[0].gutter;
  }

  removeResizer () {
    if (this._splitResizer) {
      this._splitResizer.destroy();
      this._splitResizer = null;
    }
  }

}

function emptyStore () {
  return {
    selectedFeatureIds: {}
  };
}

function addParamsToUrl (params) {
  const urlParams = new URLSearchParams(window.location.search);
  Object.keys(params).forEach(paramKey => {
    urlParams.set(paramKey, params[paramKey]);
  });
  updateUrlWithQueryParams(urlParams);
}

function updateUrlWithQueryParams (urlParams) {
  const baseUrl = window.location.href.split('?')[0];
  window.history.replaceState(null, null, `${baseUrl}?${urlParams.toString()}`);
}

function getUrlParam (paramName, defaultValue) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has(paramName) ? urlParams.get(paramName) : defaultValue;
}

function toggleParam (paramName, paramValue, isOn) {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete(paramName);
  if (isOn) {
    urlParams.set(paramName, paramValue);
  }
  updateUrlWithQueryParams(urlParams);
}

customElements.define(name, MapDashboard);
